import { useEffect, useState, type ComponentType } from 'react'
import { GoogleAnalyticsTracker } from '@/components/app/GoogleAnalyticsTracker'
import { AppToastViewport } from '@/components/app/AppToast'
import { resolveAppLocale, type AppLocale } from '@/lib/i18n'
import { AppRouter } from '@/routes/AppRouter'

const LOCAL_SERVICE_WORKER_REFRESH_KEY = 'dihaw.localServiceWorkerRefresh'

function isLocalWebHost() {
  if (typeof window === 'undefined') return false
  return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname)
}

function getBrowserLocale(): AppLocale {
  const browserTimezone = typeof window !== 'undefined'
    ? String(Intl.DateTimeFormat().resolvedOptions().timeZone || '')
    : ''
  return resolveAppLocale({ timezone: browserTimezone })
}

async function unregisterLocalServiceWorkers() {
  if (!isLocalWebHost() || !('serviceWorker' in navigator)) return

  const [registrations] = await Promise.all([
    navigator.serviceWorker.getRegistrations(),
    'caches' in window
      ? caches.keys().then((cacheKeys) => Promise.all(cacheKeys.map((cacheKey) => caches.delete(cacheKey))))
      : Promise.resolve([]),
  ])

  if (registrations.length === 0) {
    window.sessionStorage.removeItem(LOCAL_SERVICE_WORKER_REFRESH_KEY)
    return
  }

  await Promise.all(registrations.map((registration) => registration.unregister()))

  if (navigator.serviceWorker.controller && window.sessionStorage.getItem(LOCAL_SERVICE_WORKER_REFRESH_KEY) !== '1') {
    window.sessionStorage.setItem(LOCAL_SERVICE_WORKER_REFRESH_KEY, '1')
    window.location.reload()
    return
  }

  window.sessionStorage.removeItem(LOCAL_SERVICE_WORKER_REFRESH_KEY)
}

function App() {
  const [locale, setLocale] = useState<AppLocale>(() => getBrowserLocale())
  const [isUpdatePromptReady, setIsUpdatePromptReady] = useState(false)
  const [UpdatePromptComponent, setUpdatePromptComponent] = useState<ComponentType | null>(null)

  useEffect(() => {
    void unregisterLocalServiceWorkers().catch((error) => {
      console.error('Failed to clear local service workers:', error)
    })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const timerId = window.setTimeout(() => setIsUpdatePromptReady(true), 1500)
    return () => window.clearTimeout(timerId)
  }, [])

  useEffect(() => {
    if (!isUpdatePromptReady || isLocalWebHost()) return

    let isMounted = true
    void import('@/components/app/AppVersionUpdatePrompt')
      .then((module) => {
        if (isMounted) setUpdatePromptComponent(() => module.AppVersionUpdatePrompt)
      })
      .catch((error) => {
        console.error('Failed to load AppVersionUpdatePrompt:', error)
      })

    return () => {
      isMounted = false
    }
  }, [isUpdatePromptReady])

  const updatePrompt = UpdatePromptComponent ? <UpdatePromptComponent /> : null

  return (
    <>
      <GoogleAnalyticsTracker />
      <AppRouter
        locale={locale}
        onLocaleChange={setLocale}
        updatePrompt={updatePrompt}
      />
      <AppToastViewport />
    </>
  )
}

export default App
